import { ChangeDetectionStrategy, Component, Input, SimpleChanges } from '@angular/core';
import { LinkModule } from '@ng-falcon/navigation/link/link.module';
import { Autolinker, AutolinkerConfig } from 'autolinker';

interface LinkedContentPart {
    type: 'text' | 'link';
    original: string;
    transformed: string;
    id: number;
}

/**
 * Component that automatically detects URLs in plain text content and converts them into clickable links.
 * Utilizes the Autolinker library for URL detection and link generation.
 * Separates text and detected links into different parts so we can maintain all original text structure.
 * (i.e., does not convert the entire content into HTML, only the links)
 */
@Component({
    selector: 'ngf-autolinked-text',
    templateUrl: './ngf-autolinked-text.component.html',
    styleUrl: './ngf-autolinked-text.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LinkModule
    ]
})
export class NgfAutolinkedTextComponent {

    static AutolinkerConfig: AutolinkerConfig = {
                urls: true,
                email: false, // Disable email linking
                phone: false, // Disable phone number linking
                mention: false, // Disable mention linking (e.g. @username for Twitter)
                hashtag: false, // Disable hashtag linking (e.g. #topic for Twitter)
                stripPrefix: false, // Do not strip prefixes like "http://"
                stripTrailingSlash: false, // Do not strip trailing slashes from URLs
                newWindow: true, // Open links in a new window/tab
        };
    

    @Input()
    content: string = '';

    linkedContent: LinkedContentPart[] = [];

    ngOnChanges(changes: SimpleChanges) {
        if (changes['content']) {
            // Use Autolinker to parse and link URLs in the content
            const matches = Autolinker.parse(this.content, NgfAutolinkedTextComponent.AutolinkerConfig);

            if (matches.length === 0) {
                // content has no links, just render original content via template
                return;
            }

            // hold original content for slicing
            const originalContent = `${this.content}`;

            this.linkedContent = matches.reduce((acc: LinkedContentPart[], match, index, arr) => {
                // Get the offset (starting index) of the match in the original content
                const matchOffset = match.getOffset();

                const previousLink = acc.length > 0 ? acc[acc.length - 1] : null;
                const lastIndex = previousLink ? originalContent.indexOf(previousLink.original) + previousLink.original.length : 0;

                // If there is text between the last link and the current match, add it as a text part
                if (matchOffset > lastIndex) {
                    acc.push({ type: 'text', original: originalContent.slice(lastIndex, matchOffset), transformed: null, id: index });
                }

                // Add the current match as a link part
                // currently, this is a default `<a>` tag generated by Autolinker
                // TODO: we could customize this to use our `ngfLink` component instead, but would require
                // bypassing the default Angular sanitizer to allow the `ngfLink` attr on the anchor tag
                // then we can .setAttr('ngfLink', '') on the built tag
                acc.push({ type: 'link', original: match.getMatchedText(), transformed: match.buildTag().toAnchorString(), id: index });

                // If this is the last match and there is remaining text after it, add it as a text part
                if (index === arr.length - 1 && matchOffset + match.getMatchedText().length < originalContent.length) {
                    acc.push({ type: 'text', original: originalContent.slice(matchOffset + match.getMatchedText().length), transformed: null, id: index + 1 });
                }

                return acc;
            }, []);
        }
    }

}